import React, { FunctionComponent } from 'react';
import { AwardCard } from 'scenes/common/components/Awards';
import { FooterTop as CommonFooterTop } from 'scenes/common/components/Footer';
import {
  isBusinessError,
  isIneligiblePromoCodeError,
  isLoadingWithNoData,
  isPromoCodeError,
  isQuoteSaved,
} from 'scenes/common/helpers/apiData';
import { useAwards } from 'scenes/common/hooks/useAwards/useAwards';
import { useIsDesktop } from 'scenes/common/hooks/useMedia';
import { isBeforeEffectiveEndDate } from 'scenes/common/selectors/configSelectors';
import { InsuranceName } from 'scenes/common/types';
import {
  shouldShowClaimCVP,
  showCompCoveredForDetails,
  showFlybuysInfoSection,
  showFooterMarketingCampaignInformation,
  showFooterQuoteInformation,
  showHassleFreeClaims,
  showKeyDifferences,
} from 'scenes/motor/components/helpers';
import { informationLinks } from 'scenes/motor/helpers/informationLinks';
import {
  getCampaign,
  getPolicyStartDate,
  getQuoteNumber,
  getSelectedCoverType,
  getSelectedInsuranceType,
} from 'scenes/motor/selectors';
import { MotorFullPageProps, MotorFunnelState } from 'scenes/motor/types';
import {
  CompCoveredDetails,
  KeyDifferences,
  totalLossSettlementText,
} from './components';
import { getIntialAccordionActiveItems } from './footerAccordionConfig';

const displayHassleFreeClaimsComp = (funnelState: MotorFunnelState) =>
  shouldShowClaimCVP(funnelState)
    ? [
        {
          text: 'How we settle a total loss claim',
          component: totalLossSettlementText,
        },
      ]
    : [];

export const Footer: FunctionComponent<Pick<
  MotorFullPageProps,
  'funnelState'
>> = ({ funnelState }) => {
  const isDesktop = useIsDesktop();

  const hasBusinessErrors =
    isBusinessError(funnelState.apiResponses.quoteDetails) &&
    !isPromoCodeError(funnelState.apiResponses.quoteDetails) &&
    !isIneligiblePromoCodeError(funnelState.apiResponses.quoteDetails);

  const isApiQuoteSaved = isQuoteSaved(funnelState.apiResponses.quoteDetails);

  const showQuoteInformation = !hasBusinessErrors && !isApiQuoteSaved;

  const isPageNotLoadingOrError =
    !hasBusinessErrors &&
    !isApiQuoteSaved &&
    !isLoadingWithNoData(funnelState.apiResponses.quoteDetails);

  const { motorAwardsToDisplay } = useAwards();

  const displayCompCoveredForDetails = showCompCoveredForDetails(
    funnelState.step,
    getSelectedInsuranceType(funnelState)
  );

  const displayKeyDifferences = showKeyDifferences(
    funnelState.step,
    getSelectedInsuranceType(funnelState)
  );

  const displayHassleFreeClaims = showHassleFreeClaims(funnelState.flow);

  const initialAccordionActiveItems = getIntialAccordionActiveItems(
    displayCompCoveredForDetails,
    displayKeyDifferences,
    displayHassleFreeClaims,
    isDesktop
  );

  return (
    <>
      <CommonFooterTop
        quoteNumber={getQuoteNumber(funnelState)}
        campaign={getCampaign(funnelState)}
        showQuoteInformation={showQuoteInformation}
        pageNotLoadingOrError={isPageNotLoadingOrError}
        showFlybuys={showFlybuysInfoSection(
          funnelState.step,
          getSelectedCoverType(funnelState)
        )}
        showFooterQuoteInformation={showFooterQuoteInformation(
          funnelState.flow,
          funnelState.step
        )}
        showMarketingCampaign={showFooterMarketingCampaignInformation(
          funnelState.flow
        )}
        additionalComponents={[
          ...(displayKeyDifferences
            ? [
                {
                  text: `Compare ${getSelectedInsuranceType(
                    funnelState
                  )} Products`,
                  component: <KeyDifferences />,
                },
              ]
            : []),
          ...(displayCompCoveredForDetails
            ? [
                {
                  text: `Comprehensive Car Insurance Benefits`,
                  component: <CompCoveredDetails />,
                },
              ]
            : []),
          ...(displayHassleFreeClaims
            ? displayHassleFreeClaimsComp(funnelState)
            : []),
        ]}
        initialAccordionActiveItems={initialAccordionActiveItems}
        informationLinks={informationLinks}
        insuranceName={InsuranceName.Car}
        awardsToDisplay={motorAwardsToDisplay(
          funnelState
        ).map((award) => () => <AwardCard award={award} inFooter />)}
        isBeforeEffectiveEndDate={isBeforeEffectiveEndDate(
          funnelState.apiResponses.config,
          getPolicyStartDate(funnelState)
        )}
      />
    </>
  );
};

export default Footer;
